import React, { useState } from 'react';
import { signUp } from 'aws-amplify/auth';
import { Link } from 'react-router-dom';
import { useLocations } from '../../hooks/useLocations';
import { SMS_CONSENT_TEXT } from '../../constants/smsConsent';

type VerificationPath = 'email' | 'phone' | 'both';

interface SignUpFormProps {
  onSuccess?: (identifier: string, username: string, path: VerificationPath, phone: string, password: string) => void;
  onSwitchToLogin?: () => void;
}

function toE164(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (raw.trim().startsWith('+')) return '+' + digits;
  return '+1' + digits;
}

function isValidE164(val: string): boolean {
  return /^\+[1-9]\d{7,14}$/.test(val);
}

const DisplayNameTooltip: React.FC = () => (
  <span className="ml-1 text-gray-400 cursor-help group relative inline-block">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
    <span className="hidden group-hover:block absolute left-0 top-5 z-10 w-64 rounded bg-gray-800 text-white text-xs p-2 shadow-lg">
      Use your first and last name, or first name and last initial — e.g. Michael Goddard or Michael G.
    </span>
  </span>
);

const SignUpForm: React.FC<SignUpFormProps> = ({ onSuccess, onSwitchToLogin }) => {
  const { locations } = useLocations();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [defaultLocation, setDefaultLocation] = useState('');
  const [verificationPath, setVerificationPath] = useState<VerificationPath>('email');
  const [smsConsent, setSmsConsent] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const inputCls = 'mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm';
  const labelCls = 'block text-sm font-medium text-gray-700';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }

    const e164 = verificationPath !== 'email' ? toE164(phone) : '';
    if (verificationPath !== 'email' && !isValidE164(e164)) {
      setError('Enter a valid phone number, e.g. (804) 555-0100');
      return;
    }
    if (verificationPath !== 'email' && !smsConsent) {
      setError('Please agree to receive text messages to continue with phone verification.');
      return;
    }

    const notifyPref = verificationPath === 'phone' ? 'sms' : verificationPath === 'both' ? 'both' : 'email';
    const username = verificationPath === 'phone' ? e164 : email;

    const userAttributes: Record<string, string> = {
      name,
      'custom:locations': defaultLocation,
      'custom:notify_pref': notifyPref,
    };
    if (verificationPath !== 'phone') userAttributes['email'] = email;
    if (verificationPath !== 'email') userAttributes['phone_number'] = e164;

    setIsLoading(true);
    try {
      await signUp({ username, password, options: { userAttributes } });
      onSuccess?.(username, username, verificationPath, e164, password);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-center text-gray-900 mb-6">Join Pop-Up Yoga</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Display Name */}
        <div>
          <label htmlFor="name" className={labelCls}>
            Display Name *<DisplayNameTooltip />
          </label>
          <input
            id="name" type="text" value={name} onChange={e => setName(e.target.value)}
            required className={inputCls} placeholder="Michael G."
          />
        </div>

        {/* Preferred Location */}
        <div>
          <label htmlFor="defaultLocation" className={labelCls}>Preferred Location *</label>
          <select
            id="defaultLocation" value={defaultLocation}
            onChange={e => setDefaultLocation(e.target.value)}
            required className={inputCls}
          >
            <option value="">Select your home location…</option>
            {locations.map(loc => (
              <option key={loc.locationId} value={loc.locationId}>{loc.displayName}</option>
            ))}
          </select>
        </div>

        {/* Contact & Verification */}
        <fieldset className="border border-gray-200 rounded-md p-4">
          <legend className="text-sm font-medium text-gray-700 px-1">Contact &amp; Verification</legend>
          <div className="space-y-2 mb-4">
            {(['email', 'phone', 'both'] as VerificationPath[]).map(path => (
              <label key={path} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio" name="verificationPath" value={path}
                  checked={verificationPath === path}
                  onChange={() => setVerificationPath(path)}
                  className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  {path === 'email' && 'Email only'}
                  {path === 'phone' && 'Phone only (SMS)'}
                  {path === 'both'  && 'Email and phone'}
                </span>
              </label>
            ))}
          </div>

          {verificationPath !== 'phone' && (
            <div className="mb-3">
              <label htmlFor="email" className={labelCls}>Email Address *</label>
              <input
                id="email" type="email" value={email}
                onChange={e => setEmail(e.target.value)}
                required className={inputCls} placeholder="you@example.com"
              />
            </div>
          )}

          {verificationPath !== 'email' && (
            <div>
              <label htmlFor="phone" className={labelCls}>Phone Number *</label>
              <input
                id="phone" type="tel" value={phone}
                onChange={e => setPhone(e.target.value)}
                required className={inputCls} placeholder="(804) 555-0100"
              />
              <p className="mt-1 text-xs text-gray-500">US numbers assumed. Include country code for international, e.g. +44 7700 900000.</p>
            </div>
          )}

          {verificationPath !== 'email' && (
            <div className="mt-3 flex items-start gap-2">
              <input
                type="checkbox" id="smsConsent" checked={smsConsent}
                onChange={e => setSmsConsent(e.target.checked)}
                required className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="smsConsent" className="text-xs text-gray-600">
                {SMS_CONSENT_TEXT} View our{' '}
                <Link to="/terms" target="_blank" className="text-primary-600 hover:underline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank" className="text-primary-600 hover:underline">Privacy Policy</Link>.
              </label>
            </div>
          )}
        </fieldset>

        {/* Password */}
        <div>
          <label htmlFor="password" className={labelCls}>Password *</label>
          <input
            id="password" type="password" value={password}
            onChange={e => setPassword(e.target.value)}
            required className={inputCls} placeholder="Minimum 8 characters"
          />
          <p className="mt-1 text-xs text-gray-500">Must include uppercase, lowercase, and a number.</p>
        </div>

        <div>
          <label htmlFor="confirmPassword" className={labelCls}>Confirm Password *</label>
          <input
            id="confirmPassword" type="password" value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            required className={inputCls} placeholder="Repeat your password"
          />
        </div>

        <button
          type="submit" disabled={isLoading || (verificationPath !== 'email' && !smsConsent)}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Creating Account…' : 'Create Account'}
        </button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          Already have an account?{' '}
          <button onClick={onSwitchToLogin} className="font-medium text-primary-600 hover:text-primary-500">
            Sign in here
          </button>
        </p>
      </div>
    </div>
  );
};

export default SignUpForm;
